import {
  ChargeStrategyTypeEnum,
  CombinedChargeStrategyDto,
  CombinedChargeStrategyConfiguration,
  DailyEnergyConsumption,
  ChargeStrategyUnit,
} from 'src/Libs/wallboxes/types'
import { WallboxType } from 'src/Libs/wallboxes/dto'
import {
  CombinedChargeStrategyConfigurationForm,
  DailyEnergyConsumptionForm,
} from 'src/Libs/wallboxes/models/wallbox-configuration/wallbox-configuration-settings-form.model'
import {
  DEFAULT_HUMAN_TIME,
  differenceInSeconds,
  format,
  startOfDay,
  parse,
  humanTimeToSecondsFromMidnight,
  secondsFromMidnightToHumanTime,
} from '@e3dc-react/shell/Libs/date'

const VOLTAGE_FACTOR = 1000

/**
 * Transform weekday values from domain model (WallboxModel) into a data format used by a form-processor like Formik.
 */
export const weekdayToForm = (values: DailyEnergyConsumption): DailyEnergyConsumptionForm => {
  const midnight = startOfDay(new Date())
  midnight.setSeconds(values.daytime)

  return {
    daytime: format(midnight, DEFAULT_HUMAN_TIME),
    minEnergy: values.minEnergy / VOLTAGE_FACTOR,
  }
}

/**
 * Transforms weekday values produced by a form-processor like Formik into a domain model format
 */
export const formToWeekday = (values: DailyEnergyConsumptionForm): DailyEnergyConsumption => {
  const midnight = startOfDay(new Date())
  const timeOnDay = parse(values.daytime, DEFAULT_HUMAN_TIME, new Date())

  const difference = differenceInSeconds(timeOnDay, midnight)

  return {
    daytime: difference,
    minEnergy: values.minEnergy * VOLTAGE_FACTOR,
  }
}

const convertChargeStrategyValueToForm = (value: number, chargeStrategyUnit: ChargeStrategyUnit, wallboxType: WallboxType): number => {
  if (chargeStrategyUnit === 2 || wallboxType === WallboxType.EDSN) {
    // For SoC / EDSN, the unit is percentage points and no conversion is needed
    return Number(value)
  }

  return value / VOLTAGE_FACTOR
}

/**
 * Transform chargeStrategyConfiguration values from domain model (WallboxModel) into a data format used by a form-processor like Formik.
 */
export const chargeStrategyConfigurationToForm = (
  value: CombinedChargeStrategyConfiguration,
  wallboxType: WallboxType
): CombinedChargeStrategyConfigurationForm => {
  const humanTime = secondsFromMidnightToHumanTime(value.chargeStrategyDelayedChargeTime)
  const directChargeAmount = convertChargeStrategyValueToForm(value.chargeStrategyDirectChargeAmount, value.chargeStrategyUnit, wallboxType)
  const delayedChargeAmount = convertChargeStrategyValueToForm(value.chargeStrategyDelayedChargeAmount, value.chargeStrategyUnit, wallboxType)
  const totalChargeAmount = directChargeAmount + delayedChargeAmount

  return {
    chargeStrategyWeekday: value.chargeStrategyWeekday,
    chargeStrategyUnit: value.chargeStrategyUnit,
    chargeStrategyDelayedChargeTime: humanTime,
    chargeStrategyDirectChargeAmount: directChargeAmount,
    chargeStrategyDelayedChargeAmount: delayedChargeAmount,
    chargeStrategyTotalChargeAmount: totalChargeAmount,
  }
}

const convertChargeStrategyValueToConfig = (value: number, chargeStrategyUnit: ChargeStrategyUnit, wallboxType: WallboxType): number => {
  if (chargeStrategyUnit === 2 || wallboxType === WallboxType.EDSN) {
    // For SoC / EDSN, the unit is percentage points and no conversion is needed
    return Number(value)
  }

  return value * VOLTAGE_FACTOR
}

/**
 * Transforms chargeStrategyConfiguration values produced by a form-processor like Formik into a domain model format
 */
export const formToChargeStrategyConfiguration = (
  value: CombinedChargeStrategyConfigurationForm,
  wallboxType: WallboxType
): CombinedChargeStrategyConfiguration => {
  const secondsFromMidnight = humanTimeToSecondsFromMidnight(value.chargeStrategyDelayedChargeTime)

  return {
    chargeStrategyWeekday: value.chargeStrategyWeekday,
    chargeStrategyUnit: value.chargeStrategyUnit,
    chargeStrategyDirectChargeAmount: convertChargeStrategyValueToConfig(
      value.chargeStrategyDirectChargeAmount,
      value.chargeStrategyUnit,
      wallboxType
    ),
    chargeStrategyDelayedChargeAmount: convertChargeStrategyValueToConfig(
      value.chargeStrategyDelayedChargeAmount,
      value.chargeStrategyUnit,
      wallboxType
    ),
    chargeStrategyDelayedChargeTime: secondsFromMidnight,
  }
}

/**
 * Transforms chargeStrategy values produced by form into a dto
 * combined charge strategy available only in software version >= X_2024_04
 *
 * Direct: 0, (A given amount energy should directly be charged, same as 'immediate')
 * Delayed: 1, (A given amount of energy should be charged until a specific time)
 * Disabled: 2, (No charge strategy should be used)
 * Combined: 3 (A combination of direct and delayed charge strategy)
 */
export const convertChargeStrategyFormToDto = (value: CombinedChargeStrategyDto, isCombinedChargeStrategy: boolean): CombinedChargeStrategyDto => {
  return {
    ...value,
    chargeStrategyType: isCombinedChargeStrategy ? ChargeStrategyTypeEnum.combined : value.chargeStrategyType,
  }
}
